import type {
  RegisteredSuite,
  SelfTestSuiteRunResult,
  TestSuiteResult,
} from "./types";

/**
 * Central registry for in-process feature self-tests. Mirrors the iOS
 * `SelfTestRunner` enum from `kaether_for_ios/Stoat/E2EE/SelfTestRunner.swift`.
 *
 * Suites are registered by side-effect: each `XxxSelfTest.ts` file calls
 * `SelfTestRunner.register({ ... })` at top level. The runner does not import
 * suite files itself — discovery happens in `selftest/index.ts` via Vite's
 * `import.meta.glob`, so adding a new suite file is enough to wire it up; no
 * central array needs editing.
 *
 * The runner correlates with the `Signal_standard_web.md` tracking states:
 *   - Tests for `[~] To be tested` items run on every dev launch and gate the
 *     promotion of those items to `[x] Verified`.
 *   - Tests for `[x] Verified` items still run; a regression there throws in
 *     dev mode so it cannot be ignored.
 *
 * Exported as a class (rather than a hidden singleton-only) so the runner's
 * own meta self-test and any future caller that needs a fresh isolated
 * instance can construct one. Production code should still go through the
 * `SelfTestRunner` singleton at the bottom of this file.
 */
export class SelfTestRunnerClass {
  private suites: RegisteredSuite[] = [];

  /**
   * Register a suite. Idempotent on `name` — a re-registration replaces the
   * earlier one (relevant under Vite HMR).
   */
  register(suite: RegisteredSuite): void {
    const existingIdx = this.suites.findIndex((s) => s.name === suite.name);
    if (existingIdx >= 0) {
      this.suites[existingIdx] = suite;
    } else {
      this.suites.push(suite);
    }
  }

  /**
   * Reset registry. Used by headless test harnesses that re-import modules.
   */
  reset(): void {
    this.suites = [];
  }

  /**
   * Snapshot of currently-registered suite names (for diagnostics).
   */
  registeredNames(): readonly string[] {
    return this.suites.map((s) => s.name);
  }

  /**
   * Snapshot of currently-registered suites (read-only). Used by the headless
   * vitest harness to correlate each `runAll()` result with its registration's
   * `isVerified` flag without exposing the mutable internal array. The runner
   * itself only needs this for the dev-mode regression hard-throw, but exposing
   * it lets the headless harness apply the identical policy without
   * duplicating the verified-name allow-list.
   */
  registrations(): readonly RegisteredSuite[] {
    return this.suites.slice();
  }

  /**
   * Run every registered suite. Returns the per-suite results.
   *
   * Suites are run in registration order. Suite N failing does not skip
   * suite N+1 (each suite catches its own errors and returns a result),
   * so one broken feature does not blind us to others.
   *
   * In dev mode, any regression in a `isVerified` suite throws after the
   * run completes, so the dev-server error overlay surfaces the failure.
   */
  async runAll(): Promise<SelfTestSuiteRunResult[]> {
    const results: SelfTestSuiteRunResult[] = [];
    for (const reg of this.suites) {
      let result: TestSuiteResult;
      try {
        result = await reg.run();
      } catch (err) {
        // A suite that throws is itself a bug — surface it as a single
        // failing case rather than crashing the whole runner.
        result = {
          allPassed: false,
          cases: [
            {
              name: "suite threw",
              passed: false,
              detail: err instanceof Error ? err.message : String(err),
            },
          ],
        };
      }
      results.push({ name: reg.name, result });
    }

    this.printSummary(results);

    // Dev-mode hard-throw: a regression in an `isVerified: true` suite
    // surfaces as a thrown error so the dev-server overlay cannot ignore it.
    //
    // Suppressed under vitest (`MODE === "test"`) because the headless
    // harness uses `jsdom`, which lacks browser-only WebCrypto / IndexedDB
    // surfaces that several verified suites depend on. The harness in
    // `runAllSelfTests.test.ts` enforces the same regression policy via
    // `expect.fail`, so suppressing the runner-level throw is not a loss of
    // coverage in CI — it just removes a duplicate signal that interacts
    // badly with vitest's worker boundary.
    const isDev =
      typeof import.meta !== "undefined" && import.meta.env?.DEV;
    const isUnderVitest =
      typeof import.meta !== "undefined" && import.meta.env?.MODE === "test";
    if (isDev && !isUnderVitest) {
      const verifiedRegressions = results.filter((r, i) => {
        const reg = this.suites[i];
        return reg.isVerified && !r.result.allPassed;
      });
      if (verifiedRegressions.length > 0) {
        const names = verifiedRegressions.map((r) => r.name).join(", ");
        // Throw on next tick so the summary prints first.
        queueMicrotask(() => {
          throw new Error(
            `[SelfTestRunner] Verified suite regression: ${names}. ` +
              "See console output above for case-level detail.",
          );
        });
      }
    }

    return results;
  }

  private printSummary(results: readonly SelfTestSuiteRunResult[]): void {
    const passCount = results.filter((r) => r.result.allPassed).length;
    // Match iOS console-output shape so the two platforms are visually
    // comparable when running in parallel.
    // eslint-disable-next-line no-console
    console.log("================ SelfTestRunner ================");
    // eslint-disable-next-line no-console
    console.log(`Suites: ${passCount}/${results.length} passing`);
    for (const suite of results) {
      const badge = suite.result.allPassed ? "✓" : "✗";
      // eslint-disable-next-line no-console
      console.log(`${badge} ${suite.name}`);
      if (!suite.result.allPassed) {
        for (const c of suite.result.cases) {
          if (!c.passed) {
            // eslint-disable-next-line no-console
            console.log(
              `    └─ FAIL: ${c.name}${c.detail ? ` — ${c.detail}` : ""}`,
            );
          }
        }
      }
    }
    // eslint-disable-next-line no-console
    console.log("================================================");
  }
}

/**
 * Singleton instance. Suite modules import this and call `.register(...)`
 * at module top level.
 */
export const SelfTestRunner = new SelfTestRunnerClass();
